<template>
  <a-space direction="vertical" style="width:100%" size="middle">
    <a-card :bordered="false">
      <a-space wrap>
        <a-button type="primary" @click="openCreate">Добавить пользователя</a-button>
        <a-input
          v-model:value="search"
          placeholder="Поиск по имени, логину, телефону, заказчику"
          style="width:320px"
          allowClear
        />
        <a-select
          v-model:value="contractorFilter"
          :options="contractorFilterOptions"
          style="width:240px"
          placeholder="Фильтр по заказчику"
          show-search
          :filter-option="filterContractorOptions"
        />
        <a-segmented v-model:value="roleFilter" :options="roleFilterOptions" />
        <a-segmented v-model:value="activeFilter" :options="activeFilterOptions" />
        <a-button @click="load" :loading="loading">Обновить</a-button>
      </a-space>
    </a-card>

    <a-card title="Пользователи" :bordered="false">
      <a-table :dataSource="filteredRows" :columns="columns" :loading="loading" :pagination="false" rowKey="id">
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'role'">
            {{ label(record.role) }}
          </template>

          <template v-else-if="column.key === 'contractor'">
            {{ contractorName(record) }}
          </template>

          <template v-else-if="column.key === 'active'">
            <a-switch :checked="!!record.active" @change="toggleActive(record)" />
          </template>

          <template v-else-if="column.key === 'actions'">
            <a-space>
              <a-button size="small" @click="resetPassword(record)">Сбросить пароль</a-button>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal v-model:open="editOpen" title="Новый пользователь" @ok="save" :confirmLoading="saving">
      <a-form layout="vertical">
        <a-form-item label="Имя">
          <a-input v-model:value="form.name" />
        </a-form-item>

        <a-form-item label="Телефон">
          <a-input v-model:value="form.phone" />
        </a-form-item>

        <a-form-item label="Логин">
          <a-input v-model:value="form.login" />
        </a-form-item>

        <a-form-item label="Пароль">
          <a-input-password v-model:value="form.password" />
        </a-form-item>

        <a-form-item label="Роль">
          <a-select v-model:value="form.role" :options="roleOptions" />
        </a-form-item>

        <a-form-item label="Заказчик">
          <a-select v-model:value="form.contractor_id" :options="contractorOptions" />
        </a-form-item>
      </a-form>
    </a-modal>
  </a-space>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { getKitchenUsers, createKitchenUser, setKitchenUserActive, resetKitchenUserPassword } from '../../api/users'
import { getKitchenContractors } from '../../api/contractors'
import { label } from '../../utils/labels'

const loading = ref(false)
const saving = ref(false)
const editOpen = ref(false)
const rows = ref([])
const search = ref('')
const roleFilter = ref('ALL')
const activeFilter = ref('ALL')
const contractorFilter = ref('ALL')

const form = ref({
  name: '',
  phone: '',
  login: '',
  password: '',
  role: 'CUSTOMER',
  contractor_id: null,
})

const roleOptions = [
  { value: 'CUSTOMER', label: 'Заказчик' },
  { value: 'KITCHEN', label: 'Кухня' },
]

const roleFilterOptions = [
  { value: 'ALL', label: 'Все роли' },
  { value: 'CUSTOMER', label: 'Заказчики' },
  { value: 'KITCHEN', label: 'Кухня' },
]

const activeFilterOptions = [
  { value: 'ALL', label: 'Все' },
  { value: 'ACTIVE', label: 'Активные' },
  { value: 'INACTIVE', label: 'Неактивные' },
]

const contractorOptions = ref([])

const contractorFilterOptions = computed(() => [
  { value: 'ALL', label: 'Все заказчики' },
  ...contractorOptions.value,
])

const columns = [
  { title: 'Имя', dataIndex: 'name' },
  { title: 'Телефон', dataIndex: 'phone' },
  { title: 'Логин', dataIndex: 'login' },
  { title: 'Заказчик', key: 'contractor', width: 220 },
  { title: 'Роль', key: 'role', width: 140 },
  { title: 'Активен', key: 'active', width: 100 },
  { title: '', key: 'actions', width: 180 },
]

const contractorMap = computed(() =>
  Object.fromEntries(contractorOptions.value.map(option => [String(option.value), option.label]))
)

const filteredRows = computed(() => {
  const query = search.value.trim().toLowerCase()

  return rows.value.filter(row => {
    if (roleFilter.value !== 'ALL' && row.role !== roleFilter.value) {
      return false
    }

    if (activeFilter.value === 'ACTIVE' && !row.active) {
      return false
    }

    if (activeFilter.value === 'INACTIVE' && !!row.active) {
      return false
    }

    if (contractorFilter.value !== 'ALL' && String(row.contractor_id || '') !== String(contractorFilter.value)) {
      return false
    }

    if (!query) return true

    const haystack = [
      row.name,
      row.phone,
      row.login,
      contractorName(row),
      label(row.role),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return haystack.includes(query)
  })
})

async function load() {
  loading.value = true
  try {
    const [res, resContractors] = await Promise.all([
      getKitchenUsers(),
      getKitchenContractors(),
    ])

    rows.value = res.data.data || []
    contractorOptions.value = (resContractors.data.data || [])
      .filter(contractor => contractor.active === 1)
      .map(contractor => ({
        value: String(contractor.id),
        label: contractor.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка загрузки')
  } finally {
    loading.value = false
  }
}

function filterContractorOptions(input, option) {
  return String(option?.label || '').toLowerCase().includes(String(input).toLowerCase())
}

function openCreate() {
  form.value = {
    name: '',
    phone: '',
    login: '',
    password: '',
    role: 'CUSTOMER',
    contractor_id: null,
  }
  editOpen.value = true
}

async function save() {
  saving.value = true
  try {
    await createKitchenUser(form.value)
    editOpen.value = false
    message.success('Пользователь создан')
    await load()
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка создания')
  } finally {
    saving.value = false
  }
}

async function toggleActive(row) {
  try {
    await setKitchenUserActive(row.id, !row.active)
    message.success('Сохранено')
    await load()
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка изменения активности')
  }
}

async function resetPassword(row) {
  try {
    const res = await resetKitchenUserPassword(row.id)
    const temp = res?.data?.data?.temp_password
    message.success(temp ? `Новый пароль: ${temp}` : 'Пароль сброшен')
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка сброса пароля')
  }
}

function contractorName(row) {
  return (
    row.contractor_name ||
    row.contractor?.name ||
    contractorMap.value[String(row.contractor_id)] ||
    '—'
  )
}

onMounted(load)
</script>
